import type { IconProps } from '@magicschool/spellbook/components/Icon';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { SpinnerProps } from '@magicschool/spellbook/components/Spinner';
import { Spinner } from '@magicschool/spellbook/components/Spinner';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import type { TooltipProps } from '@magicschool/spellbook/components/Tooltip';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { getControlIconSize, getControlTextVariant } from '@magicschool/spellbook/lib/controls';
import { mapOrderedSlots } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as TabsPrimitive from 'radix-ui/tabs';

export type TabsListSlotProps = React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants> & {
    isLoading?: boolean;
    disabled?: boolean;
  };

export const tabsListVariants = cva('items-center data-[orientation=vertical]:items-start data-[orientation=vertical]:flex-col', {
  variants: {
    fullWidth: {
      true: 'w-full flex data-[orientation=vertical]:w-fit',
      false: 'w-fit inline-flex',
    },
    size: {
      sm: 'gap-2 rounded-control-sm',
      md: 'gap-3 rounded-control-md',
      lg: 'gap-3 rounded-control-lg',
    },
    justifyTabs: {
      stretch: 'justify-stretch',
      center: 'justify-center',
      start: 'justify-start',
      end: 'justify-end',
    },
    variant: {
      default: 'bg-transparent',
      chip: '',
    },
  },
});

export const TabsListSlot = Slot<TabsListSlotProps>({
  displayName: 'Tabs.List',
  preventOverride: true,
  renderDefault: ({ children, className, disabled, size, fullWidth, justifyTabs, variant, isLoading, ...props }) => {
    const { orderedSlots } = useOrderedSlots(children, {
      triggers: { Component: TabsTriggerSlot, props: { size, justifyTabs, variant, isLoading, disabled } },
      tooltips: { Component: TabsTooltipSlot, props: { size, justifyTabs, variant, isLoading, disabled } },
    });
    return (
      <TabsPrimitive.List
        data-spellbook-name="tabs-list"
        className={cn(tabsListVariants({ fullWidth, size, variant, justifyTabs }), className)}
        {...props}
      >
        {mapOrderedSlots(orderedSlots)}
      </TabsPrimitive.List>
    );
  },
});

export type TabsTriggerSlotProps = React.ComponentProps<typeof TabsPrimitive.Trigger> &
  VariantProps<typeof tabsTriggerVariants> & {
    isLoading?: boolean;
    disabled?: boolean;
  };

export const tabsTriggerVariants = cva(
  [
    'cursor-pointer',
    'inline-flex',
    'items-center',
    'justify-center',
    'whitespace-nowrap',
    'transition-colors',
    'shrink-0',
    '[&_svg]:shrink-0',
    'outline-none',
    'focus-visible-ring',
    'border-1',
    'border-transparent',
    'overflow-hidden',
    'text-ellipsis',
    'group/tabs-trigger',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-transparent',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:text-disabled-foreground',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:border-transparent',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:shadow-none',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:transition-none',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:focus-visible:ring-0',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:cursor-not-allowed',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:pointer-events-none',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:[&_svg]:text-disabled-foreground',
    'data-[state=active]:disabled:border-disabled-background',
    'data-[state=active]:disabled:bg-disabled-background',
    'data-[state=active]:disabled:text-disabled-foreground',
    'aria-selected:[&[aria-disabled="true"]:not([aria-busy="true"])]:border-disabled-background',
    'aria-selected:[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-disabled-background',
    'aria-selected:[&[aria-disabled="true"]:not([aria-busy="true"])]:text-disabled-foreground',
  ],
  {
    variants: {
      /** The visual appearance of the button. */
      variant: {
        default: [
          'text-default-text',
          'bg-transparent',
          '[&_svg]:text-default-icon',
          'hover:bg-surface-transparent-1',
          'active:bg-surface-transparent-2',
          'active:text-default-text-strong',
          /**
           * @TODO [SPE-16] Centralize “Selected” Color Tokens into Shared Style Utilities
           * https://linear.app/magicschool/issue/SPE-16/centralize-selected-color-tokens-into-shared-style-utilities
           */
          'data-[state=active]:bg-selected-default-background',
          'data-[state=active]:hover:bg-selected-default-background-hover',
          'data-[state=active]:active:bg-selected-default-background-active',
          'data-[state=active]:[&_svg]:text-selected-default-icon',
          'data-[state=active]:hover:[&_svg]:text-selected-default-icon-hover',
          'data-[state=active]:active:[&_svg]:text-selected-default-icon-active',
          'data-[state=active]:text-selected-default-foreground',
          'data-[state=active]:hover:text-selected-default-foreground-hover',
          'data-[state=active]:active:text-selected-default-foreground-active',
          'aria-selected:bg-selected-default-background',
          'aria-selected:hover:bg-selected-default-background-hover',
          'aria-selected:active:bg-selected-default-background-active',
          'aria-selected:[&_svg]:text-selected-default-icon',
          'aria-selected:hover:[&_svg]:text-selected-default-icon-hover',
          'aria-selected:active:[&_svg]:text-selected-default-icon-active',
          'aria-selected:text-selected-default-foreground',
          'aria-selected:hover:text-selected-default-foreground-hover',
          'aria-selected:active:text-selected-default-foreground-active',
        ],
        underline: [
          'bg-transparent',
          'border-x-0',
          'border-t-0',
          'border-b-2',
          'data-[state=active]:border-b-primary-solid-background',
          'data-[state=active]:[&_svg]:text-selected-default-icon',
          'data-[state=active]:hover:[&_svg]:text-selected-default-icon-hover',
          'data-[state=active]:active:[&_svg]:text-selected-default-icon-active',
          'data-[state=active]:text-selected-default-foreground',
          'data-[state=active]:hover:text-selected-default-foreground-hover',
          'data-[state=active]:active:text-selected-default-foreground-active',
          'aria-selected:border-b-primary-solid-background',
          'aria-selected:[&_svg]:text-selected-default-icon',
          'aria-selected:hover:[&_svg]:text-selected-default-icon-hover',
          'aria-selected:active:[&_svg]:text-selected-default-icon-active',
          'aria-selected:text-selected-default-foreground',
          'aria-selected:hover:text-selected-default-foreground-hover',
          'aria-selected:active:text-selected-default-foreground-active',
        ],
        chip: [
          'bg-surface-background-0',
          'border-border-default',
          'text-default-text',
          '[&_svg]:text-default-icon',
          'hover:bg-default-muted-background',
          'hover:[&_svg]:text-default-icon-hover',
          'data-[state=active]:bg-default-muted-background',
          'data-[state=active]:hover:bg-default-muted-background-hover',
          'data-[state=active]:border-transparent',
          'data-[state=active]:text-default-text-strong',
          'data-[state=active]:[&_svg]:text-default-icon',
          'aria-selected:bg-default-muted-background',
          'aria-selected:hover:bg-default-muted-background-hover',
          'aria-selected:border-transparent',
          'aria-selected:text-default-text-strong',
          'aria-selected:[&_svg]:text-default-icon',
        ],
      },
      /** The size of the button. */
      size: {
        sm: ['h-control-h-sm', 'rounded-control-sm', 'gap-1', 'px-1', 'py-1', 'min-w-7'],
        md: ['h-control-h-md', 'rounded-control-md', 'gap-1', 'px-2', 'py-2', 'min-w-14'],
        lg: ['h-control-h-lg', 'rounded-control-lg', 'gap-1', 'px-3', 'py-3', 'min-w-20'],
      },
      /** Whether or not the button has a border. */
      withBorder: {
        true: [
          'border-border-strong',
          'hover:border-border-strong-hover',
          'active:border-border-strong-hover',
          'data-[state=active]:border-selected-default-stroke',
          'aria-selected:border-selected-default-stroke',
          '[&[aria-disabled="true"]:not([aria-busy="true"])]:border-disabled-background',
        ],
        false: 'border-transparent',
      },
      justifyTabs: {
        stretch: 'grow-1',
        center: 'grow-0',
        start: 'grow-0',
        end: 'grow-0',
      },
      isLoading: {
        true: 'opacity-90 pointer-events-none',
      },
    },
    compoundVariants: [
      {
        variant: 'underline',
        size: ['lg', 'sm', 'md'],
        class: 'px-0 rounded-none',
      },
    ],
  },
);

export const TabsTriggerSlot = Slot<TabsTriggerSlotProps>({
  displayName: 'Tabs.Trigger',
  preventOverride: true,
  renderDefault: ({ children, className, disabled, size, variant, withBorder, justifyTabs, isLoading, onClick, ...props }) => {
    const { slots, rest } = useSlots(children, {
      leadingIcon: { Component: isLoading ? TabsSpinnerSlot : TabsLeadingIconSlot, props: { size } },
      trailingIcon: { Component: TabsTrailingIconSlot, props: { size } },
    });
    const isActuallyDisabled = disabled || isLoading;

    const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isActuallyDisabled) {
        // explicitly prevent default and stop propagation so that `asChild` components are also disabled
        event.preventDefault();
        event.stopPropagation();

        return;
      }

      onClick?.(event);
    };

    const textVariant = getControlTextVariant(size);
    return (
      <TabsPrimitive.Trigger
        data-spellbook-name="tabs-trigger"
        type="button"
        className={cn(tabsTriggerVariants({ size, variant, justifyTabs, withBorder, isLoading }), className)}
        aria-disabled={isActuallyDisabled}
        aria-busy={isLoading}
        {...props}
        onClick={handleOnClick}
      >
        {slots.leadingIcon}
        <Text
          variant={textVariant}
          className={cn(variant === 'underline' ? undefined : 'px-1', 'min-w-0 max-w-full truncate')}
          element="span"
        >
          {rest}
        </Text>
        {slots.trailingIcon}
      </TabsPrimitive.Trigger>
    );
  },
});

export const TabsTooltipSlot = Slot<
  Omit<TooltipProps, 'asToggleTip'> &
    VariantProps<typeof tabsListVariants> & {
      isLoading?: boolean;
      disabled?: boolean;
    }
>({
  displayName: 'Tabs.Tooltip',
  spellbookName: false,
  preventOverride: true,
  renderDefault: ({
    className,
    children,
    side = 'top',
    align = 'center',
    size,

    justifyTabs,
    variant,
    isLoading,
    disabled,
    ...props
  }) => {
    const { slots } = useSlots(children, {
      tooltipContent: { Component: TabsTooltipContentSlot },
      trigger: { Component: TabsTriggerSlot, props: { size, justifyTabs, variant, isLoading, disabled } },
    });

    return (
      <Tooltip className={cn('group', className)} side={side} align={align} asToggleTip={false} {...props}>
        <Tooltip.Trigger>{slots.trigger}</Tooltip.Trigger>
        {slots.tooltipContent && <Tooltip.Content>{slots.tooltipContent}</Tooltip.Content>}
      </Tooltip>
    );
  },
});

export const TabsTooltipContentSlot = Slot<TextProps>({
  displayName: 'Tabs.TooltipContent',
  preventOverride: true,
  renderDefault: ({ ...props }) => <Text data-spellbook-name="tabs-tooltip-content" variant="caption-1" element="span" {...props} />,
});

export const TabsLeadingIconSlot = Slot<IconProps>({
  displayName: 'Tabs.LeadingIcon',
  renderDefault: ({ name = 'square-dashed', color = 'inherit', size: providedSize, ...props }) => {
    const size = getControlIconSize(providedSize);
    return <Icon data-spellbook-name="tabs-leading-icon" name={name} color={color} size={size} {...props} />;
  },
});

export const TabsTrailingIconSlot = Slot<IconProps>({
  displayName: 'Tabs.TrailingIcon',
  renderDefault: ({ name = 'square-dashed', color = 'inherit', size: providedSize, ...props }) => {
    const size = getControlIconSize(providedSize);
    return <Icon data-spellbook-name="tabs-trailing-icon" name={name} color={color} size={size} {...props} />;
  },
});

export const TabsSpinnerSlot = Slot<SpinnerProps>({
  displayName: 'Tabs.Spinner',
  isRenderedByDefault: true,
  renderDefault: ({ size: providedSize, ...props }) => {
    const size = getControlIconSize(providedSize);
    return <Spinner data-spellbook-name="tabs-spinner" size={size} {...props} />;
  },
});

export type TabsContentSlotProps = React.ComponentProps<typeof TabsPrimitive.Content> & {
  enableDefaultFocus?: boolean;
};

export const TabsContentSlot = Slot<TabsContentSlotProps>({
  displayName: 'Tabs.Content',
  preventOverride: true,
  renderDefault: ({ children, className, enableDefaultFocus, ...props }) => {
    return (
      <TabsPrimitive.Content
        data-spellbook-name="tabs-content"
        className={cn('flex-1', enableDefaultFocus ? 'focus-visible-ring rounded-md' : 'outline-none', className)}
        tabIndex={enableDefaultFocus ? 0 : undefined}
        {...props}
      >
        {children}
      </TabsPrimitive.Content>
    );
  },
});
